/*!
 * inkybay-tracking.js
 * Standalone Google Ads / UTM attribution bridge for InkyBay DesignLab.
 * Loaded (deferred) as the first script tag in <head> in layout/theme.liquid,
 * so it runs as early as possible relative to other analytics. See README.md.
 * Safe to include on every page; does nothing unless CONFIG.enabled is true.
 */
(function () {
  'use strict';

  if (window.__inkybayTrackingLoaded) return;
  window.__inkybayTrackingLoaded = true;

  var CONFIG = {
    enabled: true,
    debug: false,

    // Attribution capture / storage.
    storageKey: 'inkybay_attribution_v1',
    attributionMaxAgeDays: 90,

    // InkyBay DesignLab destination matching.
    // CONFIRMED against this store's real InkyBay script
    // (inkybay.com/shopify/js/inkybay.js): DesignLab is always reached at a
    // same-origin path, `iBwOrigin + '/pages/designlab/pid-' + productId`
    // (iBwOrigin === window.location.origin). designLabDomains is kept only
    // for non-standard installs where DesignLab is genuinely cross-origin.
    designLabPathPrefixes: ['/pages/designlab'],
    designLabDomains: [],

    // CONFIRMED against the real script: the button is created as
    // `<div id="EFOLI_CUSTOMIZE_IT_BUTTON">` (not an anchor) and a second
    // variant on collection pages uses `#inkybay_collection_customizeit_btn_<id>`.
    // '#EFOLI_DESIGNER_CUSTOMIZE_IT_BUTTON_PLACE' is the container id InkyBay
    // itself looks for. Bracket selectors are defensive and only ever narrow
    // matches further, never broaden them into an unscoped global listener.
    inkybaySelectors: [
      '#EFOLI_CUSTOMIZE_IT_BUTTON',
      '#EFOLI_DESIGNER_CUSTOMIZE_IT_BUTTON_PLACE a',
      '#EFOLI_DESIGNER_CUSTOMIZE_IT_BUTTON_PLACE button',
      '[id^="EFOLI_CUSTOMIZE_IT_BUTTON"]',
      '[id^="inkybay_collection_customizeit_btn_"]'
    ],

    googleAds: {
      // Leave blank to disable conversion tracking entirely (no invented IDs).
      conversionId: '',    // e.g. 'AW-123456789'
      conversionLabel: '', // e.g. 'AbC-D_efG-h12_34i5'
      // Only used if neither gtag() nor a GTM dataLayer already exists.
      autoLoadGtag: true
    },

    // Best-effort mirror of a captured gclid into the first-party cookie
    // format Google's own gtag.js uses for click/conversion linking
    // (`_gcl_aw=GCL.<epoch_seconds>.<gclid>`). This format isn't officially
    // published by Google, so treat it as an additional safety net, not a
    // guaranteed fix — it only ever fills the cookie in if nothing fresher
    // is already there, so it can't clobber a legitimate one Google's own
    // tag already set. Host-only (no cross-subdomain Domain attribute),
    // since the registrable domain can't be safely inferred generically.
    mirrorGclidCookie: true,

    // Unconditional dataLayer events (see README) so any existing GTM
    // container can hook a Custom Event trigger to attribution becoming
    // available, independent of whether googleAds.conversionId is set.
    pushDataLayerEvents: true
  };

  var TRACKED_KEYS = ['gclid', 'gbraid', 'wbraid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gad_source', 'gad_campaignid', 'cmc_adid'];
  var CLICK_SIGNAL_KEYS = ['gclid', 'gbraid', 'wbraid', 'utm_source'];
  // Google keeps adding new auto-tagged `gad_*` parameters over time
  // (gad_source, gad_campaignid, ...); capture any of these even before
  // they've been explicitly added to TRACKED_KEYS above.
  var DYNAMIC_KEY_PATTERN = /^gad_/i;
  var MAX_AGE_MS = CONFIG.attributionMaxAgeDays * 24 * 60 * 60 * 1000;
  var lastConversionAt = 0;

  function log() {
    if (!CONFIG.debug) return;
    try { console.log.apply(console, ['[inkybay-tracking]'].concat(Array.prototype.slice.call(arguments))); } catch (e) {}
  }

  function logError(context, err) {
    if (!CONFIG.debug) return;
    try { console.warn('[inkybay-tracking]', context, err); } catch (e) {}
  }

  function sanitize(value) {
    if (typeof value !== 'string') return '';
    return value.trim().slice(0, 512).replace(/[\r\n\t\u0000-\u001f]/g, '');
  }

  function readStorage() {
    try {
      var raw = window.localStorage.getItem(CONFIG.storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      logError('readStorage', e);
      return null;
    }
  }

  function writeStorage(data) {
    try {
      window.localStorage.setItem(CONFIG.storageKey, JSON.stringify(data));
    } catch (e) {
      logError('writeStorage', e);
    }
  }

  function readValidAttribution() {
    var data = readStorage();
    if (!data || !data.capturedAt) return null;
    if (Date.now() - data.capturedAt > MAX_AGE_MS) return null;
    return data;
  }

  function readCookie(name) {
    try {
      var escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      var match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    } catch (e) {
      return null;
    }
  }

  function writeCookie(name, value, maxAgeSeconds) {
    try {
      var parts = [name + '=' + encodeURIComponent(value), 'path=/', 'max-age=' + Math.floor(maxAgeSeconds), 'SameSite=Lax'];
      if (window.location.protocol === 'https:') parts.push('Secure');
      document.cookie = parts.join('; ');
    } catch (e) {
      logError('writeCookie', e);
    }
  }

  // See CONFIG.mirrorGclidCookie for why this is best-effort, not authoritative.
  function mirrorGclidCookie(attribution) {
    if (!CONFIG.mirrorGclidCookie || !attribution || !attribution.gclid) return;
    try {
      var existing = readCookie('_gcl_aw');
      if (existing) {
        var match = /^GCL\.(\d+)\./.exec(existing);
        if (match && Number(match[1]) * 1000 >= attribution.capturedAt) return; // don't clobber a fresher/legitimate cookie
      }
      var value = 'GCL.' + Math.floor(attribution.capturedAt / 1000) + '.' + attribution.gclid;
      writeCookie('_gcl_aw', value, MAX_AGE_MS / 1000);
      log('mirrored gclid into _gcl_aw cookie (best-effort)');
    } catch (e) {
      logError('mirrorGclidCookie', e);
    }
  }

  function pushDataLayerEvent(eventName, attribution) {
    if (!CONFIG.pushDataLayerEvents) return;
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, inkybayAttribution: attribution || null });
      log('pushed dataLayer event', eventName, attribution);
    } catch (e) {
      logError('pushDataLayerEvent', e);
    }
  }

  // Policy: a new URL carrying a click id or utm_source is treated as a fresh
  // marketing touch and replaces the whole stored snapshot ("last qualifying
  // click wins" — matches Google Ads' own last-click model). A URL with none
  // of these signals never touches storage, so normal Shopify browsing between
  // pages preserves whatever was captured earlier.
  function captureAttribution() {
    var params;
    try {
      params = new URL(window.location.href).searchParams;
    } catch (e) {
      return;
    }

    var incoming = {};
    var hasSignal = false;
    TRACKED_KEYS.forEach(function (key) {
      var val = params.get(key);
      if (val) {
        incoming[key] = sanitize(val);
        if (CLICK_SIGNAL_KEYS.indexOf(key) !== -1) hasSignal = true;
      }
    });
    params.forEach(function (value, key) {
      if (incoming[key] !== undefined || !value) return;
      if (DYNAMIC_KEY_PATTERN.test(key)) incoming[key] = sanitize(value);
    });

    if (!hasSignal) return;

    incoming.capturedAt = Date.now();
    writeStorage(incoming);
    mirrorGclidCookie(incoming);
    pushDataLayerEvent('inkybay_attribution_captured', incoming);
    log('captured attribution', incoming);
  }

  function matchesPathPrefix(pathname) {
    return CONFIG.designLabPathPrefixes.some(function (prefix) {
      return pathname === prefix || pathname.indexOf(prefix + '/') === 0;
    });
  }

  function isDesignLabUrl(rawUrl) {
    var parsed;
    try {
      parsed = new URL(rawUrl, window.location.href);
    } catch (e) {
      return null;
    }
    var sameOrigin = parsed.origin === window.location.origin;
    if (sameOrigin && matchesPathPrefix(parsed.pathname)) return parsed;
    if (!sameOrigin && CONFIG.designLabDomains.some(function (domain) {
      return parsed.hostname === domain || parsed.hostname.indexOf('.' + domain) === parsed.hostname.length - domain.length - 1;
    })) return parsed;
    return null;
  }

  function appendMissingAttribution(parsed) {
    var attribution = readValidAttribution();
    if (!attribution) return null;

    var changed = false;
    // Reapply everything actually captured (fixed TRACKED_KEYS plus any
    // dynamically-captured gad_* keys), not just the fixed list, so nothing
    // captured is silently dropped on the way to DesignLab.
    Object.keys(attribution).forEach(function (key) {
      if (key === 'capturedAt') return;
      var val = attribution[key];
      if (!val) return;
      if (parsed.searchParams.get(key)) return; // never overwrite an existing valid value
      parsed.searchParams.set(key, val);
      changed = true;
    });

    return changed ? parsed : null;
  }

  function buildDesignLabUrl(rawUrl) {
    var parsed = isDesignLabUrl(rawUrl);
    if (!parsed) return null;
    var updated = appendMissingAttribution(parsed);
    return updated ? updated.toString() : null;
  }

  // Primary mechanism. InkyBay's real redirect isn't a plain anchor click in
  // every case — one path navigates via a synchronous `location = url`
  // assignment, another via an async save-then-redirect callback, and a third
  // via a custom `customizelink` JS property read in its own onclick handler.
  // None of those can be reliably rewritten before the fact across browsers.
  // But every path lands on the same origin, and this script is loaded on
  // every page (including the DesignLab page itself), so instead of
  // intercepting the outgoing navigation we correct the URL right after
  // arrival, via history.replaceState — this works regardless of which
  // internal mechanism InkyBay used to get here.
  function applyAttributionOnDesignLabLanding() {
    var current;
    try {
      current = new URL(window.location.href);
    } catch (e) {
      return;
    }
    if (!matchesPathPrefix(current.pathname)) return;

    var attribution = readValidAttribution();
    mirrorGclidCookie(attribution); // refresh even if the URL itself needs no change

    var updated = appendMissingAttribution(current);
    if (!updated) return;

    try {
      window.history.replaceState(window.history.state, '', updated.toString());
      pushDataLayerEvent('inkybay_designlab_landing', attribution);
      log('applied attribution to DesignLab URL on landing', updated.toString());
    } catch (e) {
      logError('applyAttributionOnDesignLabLanding', e);
    }
  }

  function findTrigger(target) {
    if (!CONFIG.inkybaySelectors.length || typeof target.closest !== 'function') return null;
    var selector;
    try {
      selector = CONFIG.inkybaySelectors.join(',');
      return target.closest(selector);
    } catch (e) {
      logError('findTrigger', e);
      return null;
    }
  }

  // Best-effort pre-navigation rewrite. Harmless defense-in-depth for any
  // code path that does use a real href (or the `customizelink` expando
  // property some InkyBay buttons read instead of href) — not relied on as
  // the only mechanism, since applyAttributionOnDesignLabLanding() covers the
  // cases this can't reach.
  function tryRewriteTriggerDestination(trigger) {
    var hrefEl = trigger.hasAttribute('href') ? trigger : trigger.querySelector('a[href]');
    if (hrefEl) {
      var currentHref = hrefEl.getAttribute('href');
      if (currentHref) {
        try {
          var newHref = buildDesignLabUrl(currentHref);
          if (newHref) {
            hrefEl.setAttribute('href', newHref);
            log('rewrote DesignLab href', currentHref, '->', newHref);
          }
        } catch (e) {
          logError('tryRewriteTriggerDestination href', e);
        }
      }
    }

    if (typeof trigger.customizelink === 'string' && trigger.customizelink) {
      try {
        var newLink = buildDesignLabUrl(trigger.customizelink);
        if (newLink) {
          trigger.customizelink = newLink;
          log('rewrote DesignLab customizelink', newLink);
        }
      } catch (e) {
        logError('tryRewriteTriggerDestination customizelink', e);
      }
    }
  }

  function isDebounced() {
    var now = Date.now();
    if (now - lastConversionAt < 1500) return true;
    lastConversionAt = now;
    return false;
  }

  function loadGtagAndFire(googleAds) {
    if (window.__inkybayGtagLoading) return;
    window.__inkybayGtagLoading = true;

    var script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(googleAds.conversionId);
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    window.gtag = window.gtag || gtag;
    window.gtag('js', new Date());
    window.gtag('config', googleAds.conversionId);
    fireGtagConversion(googleAds);
  }

  function fireGtagConversion(googleAds) {
    try {
      window.gtag('event', 'conversion', {
        send_to: googleAds.conversionLabel ? (googleAds.conversionId + '/' + googleAds.conversionLabel) : googleAds.conversionId
      });
      log('fired Google Ads conversion (gtag)');
    } catch (e) {
      logError('fireGtagConversion', e);
    }
  }

  // Represents "started InkyBay customization", never a purchase: no value,
  // currency or transaction_id is sent with this event.
  function fireCustomizeConversion() {
    var googleAds = CONFIG.googleAds;
    if (!googleAds || !googleAds.conversionId) return;
    if (isDebounced()) return;

    try {
      if (typeof window.gtag === 'function') {
        fireGtagConversion(googleAds);
        return;
      }
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'inkybay_customize_click',
          inkybayConversionId: googleAds.conversionId,
          inkybayConversionLabel: googleAds.conversionLabel
        });
        log('pushed dataLayer event for existing GTM container');
        return;
      }
      if (googleAds.autoLoadGtag) {
        loadGtagAndFire(googleAds);
      }
    } catch (e) {
      logError('fireCustomizeConversion', e);
    }
  }

  // InkyBay's own button binds `mouseup` + `touchstart`, not `click` — using
  // all three (plus the debounce below) makes conversion firing and the
  // best-effort rewrite reliable regardless of input device or which
  // InkyBay button variant rendered.
  function onTriggerInteraction(evt) {
    if (!CONFIG.enabled) return;

    var target = evt.target;
    if (!(target instanceof Element)) return;

    var trigger = findTrigger(target);
    if (!trigger) return;

    tryRewriteTriggerDestination(trigger);
    fireCustomizeConversion();
  }

  function wrapWindowOpen() {
    if (typeof window.open !== 'function' || window.__inkybayWindowOpenWrapped) return;
    window.__inkybayWindowOpenWrapped = true;

    var nativeOpen = window.open;
    window.open = function (url, target, features) {
      try {
        if (url) {
          var newUrl = buildDesignLabUrl(String(url));
          if (newUrl) {
            log('rewrote DesignLab window.open URL', url, '->', newUrl);
            url = newUrl;
          }
        }
      } catch (e) {
        logError('wrapWindowOpen', e);
      }
      return nativeOpen.call(window, url, target, features);
    };
  }

  function init() {
    if (!CONFIG.enabled) return;
    captureAttribution();
    applyAttributionOnDesignLabLanding();

    document.addEventListener('click', onTriggerInteraction, true);
    document.addEventListener('mouseup', onTriggerInteraction, true);
    document.addEventListener('touchstart', onTriggerInteraction, true);
    wrapWindowOpen();

    if (CONFIG.debug) {
      window.InkybayTracking = {
        config: CONFIG,
        getStoredAttribution: readValidAttribution,
        buildDesignLabUrl: buildDesignLabUrl,
        isDesignLabUrl: isDesignLabUrl
      };
    }
    log('initialized', CONFIG);
  }

  init();
})();
